/**
 * Canonicalize a document title the same way browsers tend to when reflecting
 * `document.title` (collapse whitespace, trim, NFC). Used so suffix strip/apply
 * still matches after the UA rewrites spaces in org names.
 */
export function sanitizeDocumentTitle(title: string): string {
  const normalized = typeof title.normalize === 'function' ? title.normalize('NFC') : title
  return normalized.replace(/\u0000/gu, '\uFFFD').replace(/\s+/gu, ' ').trim()
}

/**
 * Remove an org title suffix only when it matches the trailing string we
 * previously applied (or the suffix we are about to apply), compared after
 * sanitize. Does not strip from the first middot — page titles may contain
 * ` · ` in their own segments.
 *
 * Returns a sanitized base title (callers should append a sanitized suffix).
 */
export default function stripOrgDocumentTitleSuffix(
  title: string,
  appliedSuffix: string,
  desiredSuffix: string
): string {
  const sanitizedTitle = sanitizeDocumentTitle(title)
  const applied = sanitizeDocumentTitle(appliedSuffix)
  const desired = sanitizeDocumentTitle(desiredSuffix)

  if (applied && sanitizedTitle.endsWith(applied)) {
    return sanitizedTitle.slice(0, -applied.length).trimEnd()
  }
  if (desired && sanitizedTitle.endsWith(desired)) {
    return sanitizedTitle.slice(0, -desired.length).trimEnd()
  }
  return sanitizedTitle
}
